import { mutationOptions } from '@tanstack/react-query';
import { trackEvent } from '@/analytics';
import API from '@/api';
import { queryKeys } from '@/api/utils/queryKeys';
import { AnalyticsEvents } from '@/constants/analytics';
import { ErrorIds } from '@/constants/ApiResponse';
import { getConfig } from '@/constants/config';
import type { WithdrawMutationOptions } from '@/pages/Credit/queries/withdrawMutationGeneric/types';
import { withdrawMutationOptionsGeneric } from '@/pages/Credit/queries/withdrawMutationGeneric/withdrawMutationGeneric';
import { routeMap } from '@/routes/routeMap';
import { parseApiError } from '@/utils/error';

export const withdrawMutationOptions = ({ openModal, navigate }: WithdrawMutationOptions) =>
  mutationOptions({
    ...withdrawMutationOptionsGeneric,
    onSuccess: (data, amount, _onMutateResult, context) => {
      trackEvent(AnalyticsEvents.WITHDRAWAL, { value: amount, currency: getConfig().currency });

      void context.client.invalidateQueries({ queryKey: queryKeys.creditFeatures });
      void context.client.invalidateQueries({ queryKey: queryKeys.me });
      void context.client.invalidateQueries({ queryKey: queryKeys.transactions });

      void context.client.invalidateQueries({ queryKey: queryKeys.SE.paymentProtectionInsurance });
      void context.client.invalidateQueries({ queryKey: queryKeys.userPaymentProtectionInsurance });

      // Prefetch the fairness receipt
      void context.client.prefetchQuery({
        queryKey: queryKeys.withdrawalById(data.transactionId),
        queryFn: () => API.paymentProvider.getWithdrawalById(data.transactionId),
        staleTime: Infinity,
      });

      openModal('SE.WITHDRAWAL_SUCCESS', {
        isPersistedFromRedirect: true,
        payload: { transactionId: data.transactionId },
      });
      void navigate(routeMap.credit.root);
    },
    onError: (error, amount) => {
      if ('code' in error && error.code === 'ECONNABORTED') {
        openModal('WITHDRAWAL_IN_PROGRESS');
        return;
      }

      const { errorId } = parseApiError(error);

      switch (errorId) {
        case ErrorIds.INSUFFICIENT_FUNDS:
          openModal('SE.INSUFFICIENT_FUNDS');
          break;
        case ErrorIds.DIRECT_DEBIT_CHARGE_IN_PROGRESS:
          openModal('DIRECT_DEBIT_CHARGE_IN_PROGRESS');
          break;
        case ErrorIds.OVERDUE_PAYBACKS_CHARGE_IN_PROGRESS:
          openModal('OVERDUE_PAYBACKS_CHARGE_IN_PROGRESS');
          break;
        case ErrorIds.DIRECT_DEBIT_MANDATE_NOT_FOUND:
          openModal('DIRECT_DEBIT_MANDATE_NOT_FOUND');
          break;
        case ErrorIds.WITHDRAW_NOT_APPROVED:
          openModal('WITHDRAWAL_BLOCKED');
          break;
        case ErrorIds.WITHDRAW_NOT_APPROVED_USER_BLOCKED_IP:
          openModal('SE.WITHDRAWAL_IP_ADDRESS_CHECK');
          break;
        case ErrorIds.WITHDRAW_AMOUNT_GREATER_THAN_MINIMUM:
        case ErrorIds.WITHDRAW_AMOUNT_PLUS_FEE_HIGHER_THAN_LIMIT:
          openModal('WITHDRAWAL_AMOUNT_INVALID');
          break;
        default:
          openModal('WITHDRAWAL_FAIL', { payload: { amount } });
      }
    },
  });
